export type SnippetLanguage = 'curl' | 'javascript' | 'python' | 'csharp';

export const SNIPPET_LANGUAGES: { id: SnippetLanguage; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'python', label: 'Python' },
  { id: 'csharp', label: 'C#' },
];

/** Generates a runnable code sample for the current Playground request in the given
 * language — the closest a manual request builder gets to an SDK without generating
 * one (docs/KONZEPT-Developer-Platform.md lists this as the natural next step after a
 * plain request builder). */
export function generateSnippet(
  lang: SnippetLanguage, method: string, url: string, bodyText: string, token: string,
): string {
  const hasBody = !!bodyText.trim() && method !== 'GET' && method !== 'DELETE';

  switch (lang) {
    case 'curl': return curlSnippet(method, url, hasBody ? bodyText : null, token);
    case 'javascript': return jsSnippet(method, url, hasBody ? bodyText : null, token);
    case 'python': return pythonSnippet(method, url, hasBody ? bodyText : null, token);
    case 'csharp': return csharpSnippet(method, url, hasBody ? bodyText : null, token);
  }
}

function curlSnippet(method: string, url: string, body: string | null, token: string): string {
  const lines = [`curl -X ${method} "${url}" \\`, `  -H "Authorization: Bearer ${token}"`];
  if (body) {
    lines[lines.length - 1] += ' \\';
    lines.push('  -H "Content-Type: application/json" \\');
    lines.push(`  -d '${body.replace(/'/g, "'\\''")}'`);
  }
  return lines.join('\n');
}

function jsSnippet(method: string, url: string, body: string | null, token: string): string {
  const options = [
    `  method: "${method}"`,
    `  headers: { "Authorization": "Bearer ${token}"${body ? ', "Content-Type": "application/json"' : ''} }`,
  ];
  if (body) options.push(`  body: JSON.stringify(${body})`);

  return [
    `const response = await fetch("${url}", {`,
    options.join(',\n'),
    '});',
    'const data = await response.json();',
    'console.log(data);',
  ].join('\n');
}

function pythonSnippet(method: string, url: string, body: string | null, token: string): string {
  const lines = [
    'import requests',
    '',
    `response = requests.request(`,
    `    "${method}", "${url}",`,
    `    headers={"Authorization": "Bearer ${token}"},`,
  ];
  if (body) lines.push(`    json=${body},`);
  lines.push(')');
  lines.push('print(response.json())');
  return lines.join('\n');
}

function csharpSnippet(method: string, url: string, body: string | null, token: string): string {
  const lines = [
    'using var client = new HttpClient();',
    `client.DefaultRequestHeaders.Authorization = new("Bearer", "${token}");`,
    '',
  ];
  if (body) {
    lines.push(`var content = new StringContent(${JSON.stringify(body)}, Encoding.UTF8, "application/json");`);
    lines.push(`var response = await client.SendAsync(new HttpRequestMessage(HttpMethod.${methodPascalCase(method)}, "${url}") { Content = content });`);
  } else {
    lines.push(`var response = await client.SendAsync(new HttpRequestMessage(HttpMethod.${methodPascalCase(method)}, "${url}"));`);
  }
  lines.push('var body = await response.Content.ReadAsStringAsync();');
  lines.push('Console.WriteLine(body);');
  return lines.join('\n');
}

function methodPascalCase(method: string): string {
  return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
}
